import React from 'react';
import V3MapCell from '../V3MapCell';
import V3RegionCard from '../V3RegionCard';
import TerritoryOverlay from './TerritoryOverlay';

// Map constants - also used by useMapCentering hook
export const MAP_EDGE_PADDING = 20;
export const CELL_SIZE = 60;
const CELL_CHIP_FONT_SIZE = 14;
const CELL_BOTTOM_FONT_SIZE = 14;
const CELL_CHIP_ALPHA = 0.5;
const CELL_PLAYER_CHIP_ALPHA = 1.0;

/**
 * Map container with region card, map grid, cells, and territory overlay.
 *
 * @param {Object} props
 * @param {React.RefObject} props.mapContainerRef - Ref for the scrollable map container
 * @param {number} props.mapPaddingTop - Top padding in pixels
 * @param {Object|null} props.regionCard - Region card data from v3State
 * @param {React.RefObject} props.regionInfoRef - Ref for the region info div
 * @param {boolean} props.isRegionCardCollapsed - Whether region card is collapsed
 * @param {Function} props.onToggleRegionCard - Callback when region card collapse state changes
 * @param {Array|null} props.map - Map cell data array
 * @param {number|null} props.mapSize - Size of the map grid
 * @param {Object|null} props.playerArrivalCell - Cell currently animating arrival { x, y, timestamp }
 */
function V3MapContainer({
    mapContainerRef,
    mapPaddingTop,
    regionCard,
    regionInfoRef,
    isRegionCardCollapsed,
    onToggleRegionCard,
    map,
    mapSize,
    playerArrivalCell,
}) {
    return (
        <>
            {/* Fixed Region Card */}
            {regionCard && (
                <div className="region-info" ref={regionInfoRef}>
                    <V3RegionCard
                        titleLabel={regionCard.title}
                        subtitle={regionCard.subtitle}
                        imageFileName={regionCard.imageFileName}
                        peopleCount={regionCard.peopleCount || 0}
                        regionInfo={regionCard.regionInfo}
                        availableServices={regionCard.availableServices || []}
                        narrative={regionCard.narrative}
                        landWatch={regionCard.landWatch}
                        questReport={regionCard.questReport}
                        movementProgress={regionCard.movementProgress}
                        ruinsWidget={regionCard.ruinsWidget}
                        opsWidget={regionCard.opsWidget}
                        warehouseSize={regionCard.warehouseSize}
                        isCollapsed={isRegionCardCollapsed}
                        onToggleCollapse={onToggleRegionCard}
                    />
                </div>
            )}
            {/* Map Container with Dynamic Grid */}
            <div
                className="map-container"
                ref={mapContainerRef}
                style={{ paddingTop: `${mapPaddingTop - MAP_EDGE_PADDING - 60}px` }}
            >
                {mapSize && (
                    <div
                        className="map-grid"
                        style={{
                            gridTemplateColumns: `repeat(${mapSize}, ${CELL_SIZE}px)`,
                            gridTemplateRows: `repeat(${mapSize}, ${CELL_SIZE}px)`,
                            padding: `${MAP_EDGE_PADDING + 60}px ${MAP_EDGE_PADDING}px ${MAP_EDGE_PADDING}px ${MAP_EDGE_PADDING}px`,
                        }}
                    >
                        {map && (() => {
                            // Create a grid array initialized with empty cells
                            const grid = Array(mapSize).fill(null).map(() =>
                                Array(mapSize).fill(null)
                            );

                            // Place actual cells in the grid
                            map.forEach(cell => {
                                if (cell.y >= 0 && cell.y < mapSize &&
                                    cell.x >= 0 && cell.x < mapSize) {
                                    grid[cell.y][cell.x] = cell;
                                }
                            });

                            // Render the grid
                            return grid.map((row, y) =>
                                row.map((cell, x) => (
                                    <V3MapCell
                                        key={`${x}-${y}`}
                                        cell={cell || { x, y }}
                                        chipFontSize={CELL_CHIP_FONT_SIZE}
                                        bottomFontSize={CELL_BOTTOM_FONT_SIZE}
                                        chipAlpha={CELL_CHIP_ALPHA}
                                        playerChipAlpha={CELL_PLAYER_CHIP_ALPHA}
                                        shouldAnimateArrival={
                                            playerArrivalCell &&
                                            x === playerArrivalCell.x &&
                                            y === playerArrivalCell.y &&
                                            Date.now() - playerArrivalCell.timestamp < 2000
                                        }
                                    />
                                ))
                            ).flat();
                        })()}
                        {/* Territory border overlay */}
                        <TerritoryOverlay
                            cells={map}
                            mapSize={mapSize}
                            cellSize={CELL_SIZE}
                            gap={1}
                            paddingTop={MAP_EDGE_PADDING + 60}
                            paddingLeft={MAP_EDGE_PADDING}
                        />
                    </div>
                )}
            </div>
        </>
    );
}

export default V3MapContainer;
